// RollingReturnChart.tsx — Backtest's "1-Jahres-Rendite (rollierend)" curve: the fund's
// rolling 1-year annualized PRICE return over the backtest window (fundAnalytics.ts's
// rollingAnnualizedReturns, sliced to [usedFrom, usedTo] by the caller) — the "curve over
// time, 1-year average" view requested alongside the single Rendite p.a. headline number.
// One series, percent axis, zero line so above/below-zero years are obvious at a glance.
// Reuses chartZoomPlugin + measuredAxisSize like every other chart in this app.
import { useCallback, useMemo, useState } from 'preact/hooks';
import type { Options } from 'uplot';
import type { RollingReturnPoint } from '../lib/fundAnalytics.js';
import { UplotBase } from './UplotBase.js';
import { formatPercent } from '../lib/format.js';
import { zeroLinePlugin, chartZoomPlugin } from '../lib/uplot-plugins.js';
import type { ZoomState } from '../lib/uplot-plugins.js';
import { readCssVar } from '../lib/theme.js';

export interface RollingReturnChartProps {
  series: RollingReturnPoint[];
}

function toUnixSeconds(iso: string): number {
  return Math.floor(new Date(`${iso}T00:00:00Z`).getTime() / 1000);
}

export function RollingReturnChart({ series }: RollingReturnChartProps) {
  const [zoom, setZoom] = useState<ZoomState | null>(null);

  const data = useMemo((): [number[], number[]] => {
    const x = series.map((p) => toUnixSeconds(p.date));
    // Percentage points (not a raw fraction) for a readable axis, formatted back through
    // formatPercent (v/100) in the series' own value() so hover tooltips match the rest
    // of the app's "de-AT, one decimal" percent style exactly.
    const y = series.map((p) => p.annualizedReturn * 100);
    return [x, y];
  }, [series]);

  const buildOptions = useCallback(
    (width: number): Options => {
      const accent = readCssVar('--accent', '#60a5fa');
      return {
        width,
        height: 180,
        padding: [8, 8, 0, 0],
        legend: { show: true },
        cursor: { drag: { x: true, y: true }, points: { size: 5 } },
        scales: {
          x: { time: true },
          y: { range: (_u, initMin, initMax) => [Math.min(0, initMin), Math.max(0, initMax)] },
        },
        axes: [
          {
            stroke: '#93a0b8',
            grid: { stroke: 'rgba(232,236,244,0.08)', width: 1 },
            ticks: { stroke: 'rgba(232,236,244,0.15)', width: 1 },
          },
          {
            stroke: '#93a0b8',
            grid: { stroke: 'rgba(232,236,244,0.08)', width: 1 },
            ticks: { stroke: 'rgba(232,236,244,0.15)', width: 1 },
            values: (_u, ticks) => ticks.map((t) => `${t.toFixed(1)} %`),
          },
        ],
        series: [
          { label: 'Fensterbeginn' },
          {
            label: '1J-Rendite p.a.',
            stroke: accent,
            width: 2,
            value: (_u, v) => (v == null ? '—' : formatPercent(v / 100)),
          },
        ],
        plugins: [zeroLinePlugin(), chartZoomPlugin(setZoom)],
      };
    },
    [setZoom],
  );

  if (series.length === 0) {
    return (
      <div class="empty-state">
        Zu wenig Kurshistorie für eine rollierende 1-Jahres-Rendite in diesem Zeitraum.
      </div>
    );
  }

  return (
    <div class="chart-wrap">
      <UplotBase data={data} buildOptions={buildOptions} height={180} />
      {zoom?.zoomed ? (
        <div class="chart-zoom-row">
          <button type="button" class="chart-zoom-reset" onClick={() => zoom.reset()}>
            Zoom zurücksetzen
          </button>
        </div>
      ) : null}
    </div>
  );
}
